import React, { useEffect, useMemo, useState } from 'react';
import {
  EOrderType,
  Filters,
  INITIAL_FILTERS,
  IOpenOrdersTableDataSource,
  SalesOrderItem,
  SalesOrderItemTable
} from '../../../../store/ducks/new-long-steel-orders/types';
import {
  ECustomerTypeView,
  EUserPreferenceType,
  IOrderViewPreference,
  ISaveUser
} from '../../../../store/ducks/user/types';
import {
  TableContainer,
  TableOrderNumberSelectable,
  TableCell,
  TableHeaderContainer,
  TableHeaderContainerButtons,
  TableHeaderActionButton,
  OrdersTableContainer,
  MessageWithoutEmitter
} from './styles';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { ApplicationState } from '../../../../store';
import Table from '../../../../components/Table';
import { Button, Tooltip } from '@gerdau/hefesto';
import { TableRowSelection } from 'antd/lib/table/interface';
import { addToast } from '../../../../store/ducks/toast/actions';
import { EToastTypes } from '../../../../store/ducks/toast/types';

import { InformationPanel } from '@gerdau/hefesto-mais-web';
import { Icon } from '@gerdau/hefesto';
import { TPanelOptions } from '@gerdau/hefesto-mais-web/InformationPanel/InformationPanel.types';
import useDebounce from '../../../../hooks/useDebounce';
import OrdersPersonalizeModal from '../OrdersPersonalizeModal';
import { actionsOptions, HeaderColumnProps, SortDirection } from './types';
import { HeaderColumnTable } from './HeaderColumnTable';
import { ENewLongSteelOrdersRoutes } from '..';
import { EOrdersRoutes } from '../../orders.routes';
import { useHistory } from 'react-router-dom';
import { convertFilteredOrdersToIOpenOrdersTableDataSource } from '../../../../store/ducks/new-long-steel-orders';
import MessageChooseContract from '../../../../components/MessageChooseContract';
import { saveUser } from '../../../../store/ducks/user/actions';
import {
  downloadOrderItems,
  updateOrdersFiltered,
  validationUpdateItems
} from '../../../../store/ducks/new-long-steel-orders/actions';
import { ERoutes } from '../../../../routes';
import { EPurchaseRoutes } from '../../../Purchase/purchase.routes';
import DeliveryStatusColumn from './DeliveryStatusColumn';
import { sendTagging } from '../../../../store/ducks/tagging/actions';
import moment from 'moment';
import { EOrderTab } from '../../../../store/ducks/long-steel-orders/types';

const orderViewPreferenceDefault: IOrderViewPreference = {
  name: EUserPreferenceType.ORDER_VIEW,
  toggles: [
    {
      value: 'salesOrder',
      checked: true
    },
    {
      value: 'sku',
      checked: true
    },
    {
      value: 'customerSKU',
      checked: false
    },
    {
      value: 'materialDescription',
      checked: true
    },
    {
      value: 'creationDateTable',
      checked: true
    },
    {
      value: 'requestedDateTable',
      checked: true
    },
    {
      value: 'futureDeliveriesTable',
      checked: true
    },
    {
      value: 'emitter',
      checked: true
    },
    {
      value: 'receiver',
      checked: true
    },
    {
      value: 'customerOrder',
      checked: true
    },
    {
      value: 'salesOrderItem',
      checked: true
    },
    {
      value: 'divisionDescription',
      checked: true
    },
    {
      value: 'totalQuantityRequested',
      checked: true
    },
    {
      value: 'totalQuantityConfirmed',
      checked: true
    },
    {
      value: 'totalQuantityPending',
      checked: true
    },
    {
      value: 'totalQuantityDelivery',
      checked: true
    },
    {
      value: 'totalQuantityShipment',
      checked: true
    },
    {
      value: 'totalQuantityBilled',
      checked: true
    },
    {
      value: 'meltShop',
      checked: true
    },
    {
      value: 'rollingMill',
      checked: true
    },
    {
      value: 'rollingFinish',
      checked: true
    },
    {
      value: 'finish',
      checked: true
    },
    {
      value: 'stockMill',
      checked: true
    },
    {
      value: 'transit',
      checked: true
    },
    {
      value: 'stockWarehouse',
      checked: true
    },
    {
      value: 'futureDeliveriesDate',
      checked: true
    },
    {
      value: 'futureDeliveriesQuantity',
      checked: true
    },
    {
      value: 'futureDeliveriesUnit',
      checked: true
    }
  ]
};

const genericToSuffixMap: { [key: string]: string } = {
  futureDeliveriesDate: '_date',
  futureDeliveriesQuantity: '_quantity',
  futureDeliveriesUnit: '_unit'
};

const keyMappings: { [key: string]: string } = {
  _date: 'futureDeliveriesDate',
  _quantity: 'futureDeliveriesQuantity',
  _unit: 'futureDeliveriesUnit'
};

const getTargetKeyFromMap = (columnKey: string): string => {
  const foundSuffix = Object.keys(keyMappings).find((suffix) =>
    columnKey.includes(suffix)
  );
  return foundSuffix ? keyMappings[foundSuffix] : columnKey;
};

export const flatSteelItems = [
  'meltShop',
  'rollingMill',
  'rollingFinish',
  'finish',
  'stockMill',
  'transit',
  'stockWarehouse'
];

export type materialCodeType = 'sku' | 'customerSKU';
export interface OrderPreferencesProps {
  columnsHidden: string[];
  materialCode: materialCodeType;
}

const createDynamicDeliveryFilters = (
  numberOfDeliveries: number
): { [key: string]: string } => {
  const dynamicFilters: { [key: string]: string } = {};

  for (let i = 0; i < numberOfDeliveries; i++) {
    dynamicFilters[`futureDelivery_${i}_date`] = '';
    dynamicFilters[`futureDelivery_${i}_quantity`] = '';
    dynamicFilters[`futureDelivery_${i}_unit`] = '';
  }

  return dynamicFilters;
};

export const OrdersTable = ({
  hasInitialized
}: {
  hasInitialized: boolean;
}) => {
  const maxSelectedRows = 10;
  const dispatch = useDispatch();
  const history = useHistory();

  const ordersState = useSelector(
    (state: ApplicationState) => state.newLongSteelOrders
  );
  const ordersItems = useSelector(
    (state: ApplicationState) =>
      state.newLongSteelOrders[
        state.newLongSteelOrders.tableType || EOrderTab.OPEN
      ].items,
    shallowEqual
  );
  const customersState = useSelector(
    (state: ApplicationState) => state.customers
  );
  const customerViewType = useSelector(
    (state: ApplicationState) => state.user.searchCustomerType
  );
  const user = useSelector((state: ApplicationState) => state.user);

  const getUserOrderOrderPreference = (): OrderPreferencesProps => {
    const orderEntryIdx = user.preferences.findIndex(
      (preference) => preference.name === EUserPreferenceType.ORDER_VIEW
    );

    const hasPreferences = user.preferences[
      orderEntryIdx
    ] as IOrderViewPreference;

    if (orderEntryIdx !== -1 && hasPreferences && hasPreferences.toggles) {
      const columnsHidden = hasPreferences.toggles
        .filter((item) => !item.checked)
        .map((item) => item.value);

      const materialCode: materialCodeType = hasPreferences.toggles.find(
        (item) => item.value === 'sku' && !item.checked
      )
        ? 'customerSKU'
        : 'sku';

      return {
        columnsHidden,
        materialCode
      };
    }

    const columnsHidden = orderViewPreferenceDefault.toggles
      .filter((item) => !item.checked)
      .map((item) => item.value);

    const materialCode: materialCodeType =
      orderViewPreferenceDefault.toggles.find(
        (item) => item.value === 'sku' && !item.checked
      )
        ? 'customerSKU'
        : 'sku';

    return {
      columnsHidden,
      materialCode
    };
  };

  const [openOrdersData, setOpenOrdersData] = useState<SalesOrderItemTable[]>(
    []
  );
  const [orderedSales, setOrderedSales] = useState<SalesOrderItemTable[]>([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

  const [futureDeliveriesColumnQty, setFutureDeliveriesColumnQty] = useState(0);

  const [orderPreferences, setOrderPreferences] =
    useState<OrderPreferencesProps>(getUserOrderOrderPreference());

  const [filters, setFilters] = useState<Filters>(INITIAL_FILTERS);

  const [modal, setModal] = useState({
    personalizeTable: false,
    sideOptionsDownload: false,
    request: false,
    itemsUnavailable: false,
    confirm: false,
    finish: false
  });

  const activeToggles = useMemo(() => {
    const userPreference = user.preferences.find(
      (preference) => preference.name === EUserPreferenceType.ORDER_VIEW
    ) as IOrderViewPreference | undefined;

    return userPreference?.toggles ?? orderViewPreferenceDefault.toggles;
  }, [user.preferences]);

  const finalHideColumns = useMemo(() => {
    const columnsToHide = new Set<string>(orderPreferences.columnsHidden);

    const uncheckedGenericToggles = activeToggles
      .filter(
        (toggle) =>
          !toggle.checked &&
          Object.keys(genericToSuffixMap).includes(toggle.value)
      )
      .map((toggle) => toggle.value);

    uncheckedGenericToggles.forEach((genericKey) => {
      const suffix = genericToSuffixMap[genericKey];
      if (suffix) {
        for (let i = 0; i < futureDeliveriesColumnQty; i++) {
          columnsToHide.add(`futureDelivery_${i}${suffix}`);
        }
      }
    });

    if (!orderedSales.some((item) => item.orderType === EOrderType.PLANOS)) {
      flatSteelItems.forEach((item) => columnsToHide.add(item));
    }
    if (
      customerViewType === ECustomerTypeView.RECEIVER ||
      customerViewType === ECustomerTypeView.FABRICATION_RECEIVER
    ) {
      columnsToHide.add('emitter');
      columnsToHide.add('receiver');
    }

    return Array.from(columnsToHide);
  }, [
    activeToggles,
    orderPreferences.columnsHidden,
    futureDeliveriesColumnQty,
    orderedSales,
    customerViewType
  ]);

  const handleInputChange = ({
    column,
    value
  }: {
    column: keyof IOpenOrdersTableDataSource;
    value: unknown;
  }) => {
    const updatedFilters = {
      ...filters,
      [column]: value
    };

    setFilters(updatedFilters);
    filterData(updatedFilters);

    if (value) {
      dispatch(
        sendTagging({
          event: 'busca_por_coluna',
          termo_buscado: value,
          screenName: 'LG:MeusPedidos',
          itemDescription: 'meus_pedidos_acosLongos',
          estado_emissor:
            ordersState[
              ordersState.tableType || EOrderTab.OPEN
            ].initialInputValues.states.join(', '),
          emissor_pedido: customersState.customers
            .filter((item) =>
              ordersState[
                ordersState.tableType || EOrderTab.OPEN
              ].initialInputValues.emitterIds.includes(item.customerId)
            )
            .map((item) => item.name)
            .join(', '),
          data_inicio: moment(
            ordersState[ordersState.tableType || EOrderTab.OPEN]
              .initialInputValues.startDate
          ).format('DD/MM/YYYY'),
          data_fim: moment(
            ordersState[ordersState.tableType || EOrderTab.OPEN]
              .initialInputValues.endDate
          ).format('DD/MM/YYYY')
        })
      );
    }
  };

  const filterData = (filters: Filters) => {
    const result =
      openOrdersData?.filter((item: Filters) => {
        return Object.entries(filters).every(([key, value]) => {
          if (!value || typeof value !== 'string') return true;

          const itemValue = item[key as keyof Filters];

          if (typeof itemValue !== 'string') {
            return false;
          }

          if (typeof itemValue === 'string') {
            return itemValue.toLowerCase().includes(value.toLowerCase());
          }

          return itemValue === value;
        });
      }) ?? [];

    setOrderedSales(result);
    dispatch(updateOrdersFiltered({ orders: result }));
  };

  const loadingState = customersState.loading || ordersState.loading;

  const debounceFilter = useDebounce(handleInputChange, 500);
  const debounceSavePreferences = useDebounce(
    (e: OrderPreferencesProps) => handleUpdatePreferences(e),
    300
  );

  const sortOpenOrders = (openOrders: SalesOrderItemTable[]) => {
    return openOrders.sort((current, next) => {
      return current.creationDate > next.creationDate
        ? -1
        : current.creationDate < next.creationDate
        ? 1
        : 0;
    });
  };

  useEffect(() => {
    const orders: SalesOrderItemTable[] =
      convertFilteredOrdersToIOpenOrdersTableDataSource(
        ordersItems as SalesOrderItem[]
      );
    const sortData = sortOpenOrders(orders);
    setOpenOrdersData(sortData);
    setOrderedSales(sortData);

    const futureDeliveryQty = sortData.reduce((maxCount, order) => {
      const currentLength = order.futureDeliveries?.length ?? 0;
      return Math.max(maxCount, currentLength);
    }, 0);

    if (futureDeliveryQty > 0) {
      const dynamicFilters = createDynamicDeliveryFilters(futureDeliveryQty);

      setFutureDeliveriesColumnQty(futureDeliveryQty);
      setFilters({ ...INITIAL_FILTERS, ...dynamicFilters });
    } else {
      setFutureDeliveriesColumnQty(0);
      setFilters(INITIAL_FILTERS);
    }
    dispatch(updateOrdersFiltered({ orders: sortData }));
    if (sortData && sortData.length > 0) {
      dispatch(
        sendTagging({
          event: 'tabela_carregada',
          screenName: 'LG:MeusPedidos',
          itemDescription: 'meus_pedidos_acosLongos',
          estado_emissor:
            ordersState[
              ordersState.tableType || EOrderTab.OPEN
            ].initialInputValues.states.join(', '),
          emissor_pedido: customersState.customers
            .filter((item) =>
              ordersState[
                ordersState.tableType || EOrderTab.OPEN
              ].initialInputValues.emitterIds.includes(item.customerId)
            )
            .map((item) => item.name)
            .join(', '),
          data_inicio: moment(
            ordersState[ordersState.tableType || EOrderTab.OPEN]
              .initialInputValues.startDate
          ).format('DD/MM/YYYY'),
          data_fim: moment(
            ordersState[ordersState.tableType || EOrderTab.OPEN]
              .initialInputValues.endDate
          ).format('DD/MM/YYYY')
        })
      );
    }
  }, [ordersItems]);

  const handleSort = (
    key: keyof IOpenOrdersTableDataSource,
    direction: SortDirection,
    parser?: (val: unknown) => any
  ) => {
    const sorted = [...openOrdersData].sort((a, b) => {
      let valA = a[key];
      let valB = b[key];

      if (valA == null) return direction === 'asc' ? -1 : 1;
      if (valB == null) return direction === 'asc' ? 1 : -1;

      if (parser) {
        valA = parser(valA);
        valB = parser(valB);
      }

      if (valA instanceof Date && valB instanceof Date) {
        return direction === 'asc'
          ? valA.getTime() - valB.getTime()
          : valB.getTime() - valA.getTime();
      }

      if (typeof valA === 'number' && typeof valB === 'number') {
        return direction === 'asc' ? valA - valB : valB - valA;
      }

      if (typeof valA === 'boolean' && typeof valB === 'boolean') {
        return direction === 'asc'
          ? Number(valA) - Number(valB)
          : Number(valB) - Number(valA);
      }

      // Default: string
      return direction === 'asc'
        ? String(valA).localeCompare(String(valB))
        : String(valB).localeCompare(String(valA));
    });

    setOrderedSales(sorted);
    dispatch(updateOrdersFiltered({ orders: sorted }));
  };

  const getColumnOptions = ({
    key,
    sort,
    parser,
    disabledHiddenOpt
  }: {
    key: keyof IOpenOrdersTableDataSource;
    sort: boolean;
    parser?: (val: any) => unknown;
    disabledHiddenOpt?: boolean;
  }) => {
    const options: TPanelOptions[] = [
      {
        text: 'Ordenar decrescente',
        handleOnClick: () => handleSort(key, 'desc', parser)
      },
      {
        text: 'Ordenar crescente',
        handleOnClick: () => handleSort(key, 'asc', parser)
      },
      {
        text: 'Ocultar este campo',
        disabled: !!disabledHiddenOpt,
        handleOnClick: () => {
          const futureDeliveriesColumn =
            getTargetKeyFromMap(key as string) || (key as string);

          const newPreferences = {
            ...orderPreferences,
            columnsHidden: [
              ...orderPreferences.columnsHidden,
              futureDeliveriesColumn
            ]
          };
          setOrderPreferences(newPreferences);
          debounceSavePreferences(newPreferences);
        }
      }
    ];
    return sort ? options : options.slice(2);
  };

  const handleAccessOrder = (salesOrder: number) => {
    if (!salesOrder) {
      return null;
    }

    const orders = orderedSales.filter(
      (ord) => Number(ord.salesOrder) === Number(salesOrder)
    );

    if (orders && orders.length > 0) {
      history.push(
        `${EOrdersRoutes.LONG_STEEL_ORDERS}${ENewLongSteelOrdersRoutes.DETAILS}/${salesOrder}`
      );
    }
  };

  const getColumns = () => {
    const columns = [
      {
        title: (
          <HeaderColumnTable
            {...{
              width: 192,
              title: 'Nº da ordem de venda',
              column: 'salesOrder',
              value: filters.salesOrder,
              options: getColumnOptions({
                key: 'salesOrder',
                sort: true,
                parser: (val: string) => Number(val),
                disabledHiddenOpt: true
              }),
              debounce: (e) => debounceFilter(e)
            }}
          />
        ),
        dataIndex: 'salesOrder',
        render: (salesOrder: string) => (
          <TableOrderNumberSelectable
            onClick={() => handleAccessOrder(Number(salesOrder))}
          >
            {salesOrder}
          </TableOrderNumberSelectable>
        )
      },
      ...(orderPreferences.materialCode === 'sku'
        ? [
            {
              title: (
                <HeaderColumnTable
                  {...{
                    width: 147,
                    title: 'Código do material',
                    column: 'sku',
                    value: filters.sku,
                    options: getColumnOptions({
                      key: 'sku',
                      sort: true,
                      parser: (val: string) => Number(val),
                      disabledHiddenOpt: true
                    }),
                    debounce: (e) => debounceFilter(e)
                  }}
                />
              ),
              dataIndex: 'sku',
              render: (sku: string) => <TableCell>{sku}</TableCell>
            }
          ]
        : [
            {
              title: (
                <HeaderColumnTable
                  {...{
                    width: 140,
                    title: 'Código interno',
                    column: 'customerSKU',
                    value: filters.customerSKU,
                    options: getColumnOptions({
                      key: 'customerSKU',
                      sort: true,
                      disabledHiddenOpt: true
                    }),
                    debounce: (e) => debounceFilter(e)
                  }}
                />
              ),
              dataIndex: 'customerSKU',
              render: (customerSKU: string) => (
                <TableCell>{customerSKU ? customerSKU : ' - '}</TableCell>
              )
            }
          ]),
      {
        title: (
          <HeaderColumnTable
            {...{
              width: 256,
              title: 'Descrição do material',
              column: 'materialDescription',
              value: filters.materialDescription,
              options: getColumnOptions({
                key: 'materialDescription',
                sort: true,
                parser: (val: string) => Number(val)
              }),
              debounce: (e) => debounceFilter(e)
            }}
          />
        ),
        dataIndex: 'materialDescription',
        render: (materialDescription: string) => (
          <TableCell>{materialDescription}</TableCell>
        )
      },
      {
        title: (
          <HeaderColumnTable
            {...{
              width: 140,
              title: 'Data de criação',
              column: 'creationDateTable',
              value: filters.creationDateTable,
              options: getColumnOptions({
                key: 'creationDateTable',
                sort: true,
                parser: (val: string) => new Date(val)
              }),
              debounce: (e) => debounceFilter(e)
            }}
          />
        ),
        dataIndex: 'creationDateTable',
        render: (creationDateTable: string) => (
          <TableCell>{creationDateTable}</TableCell>
        )
      },
      {
        title: (
          <HeaderColumnTable
            {...{
              width: 140,
              title: 'Data desejada',
              column: 'requestedDateTable',
              value: filters.requestedDateTable,
              options: getColumnOptions({
                key: 'requestedDateTable',
                sort: true,
                parser: (val: string) => new Date(val)
              }),
              debounce: (e) => debounceFilter(e)
            }}
          />
        ),
        dataIndex: 'requestedDateTable',
        render: (requestedDateTable: string) => (
          <TableCell>{requestedDateTable}</TableCell>
        )
      },
      {
        title: (
          <HeaderColumnTable
            {...{
              width: 220,
              title: 'Status do produto',
              column: 'futureDeliveriesTable',
              value: filters.futureDeliveriesTable,
              options: getColumnOptions({
                key: 'futureDeliveriesTable',
                sort: true
              }),
              debounce: (e) => debounceFilter(e)
            }}
          />
        ),
        dataIndex: 'futureDeliveriesTable',
        render: (futureDeliveriesTable: string) => (
          <DeliveryStatusColumn data={futureDeliveriesTable} />
        )
      },
      {
        title: (
          <HeaderColumnTable
            {...{
              width: 256,
              title: 'Emissor',
              column: 'emitter',
              value: filters.emitter,
              options: getColumnOptions({
                key: 'emitter',
                sort: true
              }),
              debounce: (e) => debounceFilter(e)
            }}
          />
        ),
        dataIndex: 'emitter',
        render: (emitter: string) => <TableCell>{emitter}</TableCell>
      },
      {
        title: (
          <HeaderColumnTable
            {...{
              width: 256,
              title: 'Recebedor',
              column: 'receiver',
              value: filters.receiver,
              options: getColumnOptions({
                key: 'receiver',
                sort: true
              }),
              debounce: (e) => debounceFilter(e)
            }}
          />
        ),
        dataIndex: 'receiver',
        render: (name: string) => <TableCell>{name}</TableCell>
      },
      {
        title: (
          <HeaderColumnTable
            {...{
              width: 153,
              title: 'Identificação do pedido',
              column: 'customerOrder',
              value: filters.customerOrder,
              options: getColumnOptions({
                key: 'customerOrder',
                sort: true,
                parser: (val: string) => Number(val)
              }),
              debounce: (e) => debounceFilter(e)
            }}
          />
        ),
        dataIndex: 'customerOrder',
        render: (customerOrder: string) => (
          <TableCell>{customerOrder}</TableCell>
        )
      },
      // Essa informação não existe no momento
      // Não há número do pedido. apenas OVs
      // {
      //   title: (
      //     <HeaderColumnTable
      //       {...{
      //         width: 192,
      //         title: 'Número do pedido',
      //         column: 'numberOrder',
      //         value: filters.numberOrder,
      //         options: getColumnOptions({
      //           key: 'numberOrder',
      //           sort: true,
      //           parser: (val: string) => Number(val)
      //         }),
      //         debounce: (e) => debounceFilter(e)
      //       }}
      //     />
      //   ),
      //   dataIndex: 'numberOrder',
      //   render: (numberOrder: string) => <TableCell>{numberOrder}</TableCell>
      // },
      {
        title: (
          <HeaderColumnTable
            {...{
              width: 128,
              title: 'Nº do item',
              column: 'salesOrderItem',
              value: filters.salesOrderItem,
              options: getColumnOptions({
                key: 'salesOrderItem',
                sort: true
              }),
              debounce: (e) => debounceFilter(e)
            }}
          />
        ),
        dataIndex: 'salesOrderItem',
        render: (salesOrderItem: string) => (
          <TableCell>{salesOrderItem}</TableCell>
        )
      },
      {
        title: (
          <HeaderColumnTable
            {...{
              width: 160,
              title: 'Tipo de produto',
              column: 'divisionDescription',
              value: filters.divisionDescription,
              options: getColumnOptions({
                key: 'divisionDescription',
                sort: true
              }),
              debounce: (e) => debounceFilter(e)
            }}
          />
        ),
        dataIndex: 'divisionDescription',
        render: (divisionDescription: string) => (
          <TableCell>{divisionDescription}</TableCell>
        )
      },
      {
        title: (
          <HeaderColumnTable
            {...{
              width: 160,
              title: 'Quantidade solicitada',
              column: 'totalQuantityRequested',
              value: filters.totalQuantityRequested,
              options: getColumnOptions({
                key: 'totalQuantityRequested',
                sort: true
              }),
              debounce: (e) => debounceFilter(e)
            }}
          />
        ),
        dataIndex: 'totalQuantityRequested',
        render: (totalQuantityRequested: string) => (
          <TableCell>{totalQuantityRequested}</TableCell>
        )
      },
      {
        title: (
          <HeaderColumnTable
            {...{
              width: 160,
              title: 'Quantidade confirmada',
              column: 'totalQuantityConfirmed',
              value: filters.totalQuantityConfirmed,
              options: getColumnOptions({
                key: 'totalQuantityConfirmed',
                sort: true
              }),
              debounce: (e) => debounceFilter(e)
            }}
          />
        ),
        dataIndex: 'totalQuantityConfirmed',
        render: (totalQuantityConfirmed: string) => (
          <TableCell>{totalQuantityConfirmed}</TableCell>
        )
      },
      {
        title: (
          <HeaderColumnTable
            {...{
              width: 160,
              title: 'Saldo',
              column: 'totalQuantityPending',
              value: filters.totalQuantityPending,
              options: getColumnOptions({
                key: 'totalQuantityPending',
                sort: true
              }),
              debounce: (e) => debounceFilter(e)
            }}
          />
        ),
        dataIndex: 'totalQuantityPending',
        render: (totalQuantityPending: string) => (
          <TableCell>{totalQuantityPending}</TableCell>
        )
      },
      {
        title: (
          <HeaderColumnTable
            {...{
              width: 160,
              title: 'Em remessa',
              column: 'totalQuantityDelivery',
              value: filters.totalQuantityDelivery,
              options: getColumnOptions({
                key: 'totalQuantityDelivery',
                sort: true
              }),
              debounce: (e) => debounceFilter(e)
            }}
          />
        ),
        dataIndex: 'totalQuantityDelivery',
        render: (totalQuantityDelivery: string) => (
          <TableCell>{totalQuantityDelivery}</TableCell>
        )
      },
      {
        title: (
          <HeaderColumnTable
            {...{
              width: 160,
              title: 'Em carregamento',
              column: 'totalQuantityShipment',
              value: filters.totalQuantityShipment,
              options: getColumnOptions({
                key: 'totalQuantityShipment',
                sort: true
              }),
              debounce: (e) => debounceFilter(e)
            }}
          />
        ),
        dataIndex: 'totalQuantityShipment',
        render: (totalQuantityShipment: string) => (
          <TableCell>{totalQuantityShipment}</TableCell>
        )
      },
      {
        title: (
          <HeaderColumnTable
            {...{
              width: 160,
              title: 'Faturado',
              column: 'totalQuantityBilled',
              value: filters.totalQuantityBilled,
              options: getColumnOptions({
                key: 'totalQuantityBilled',
                sort: true
              }),
              debounce: (e) => debounceFilter(e)
            }}
          />
        ),
        dataIndex: 'totalQuantityBilled',
        render: (totalQuantityBilled: string) => (
          <TableCell>{totalQuantityBilled}</TableCell>
        )
      },
      {
        title: (
          <HeaderColumnTable
            {...{
              width: 160,
              title: 'Aciaria',
              column: 'meltShop',
              value: String(filters.meltShop),
              options: getColumnOptions({
                key: 'meltShop',
                sort: true
              }),
              debounce: (e) => debounceFilter(e)
            }}
          />
        ),
        dataIndex: 'meltShop',
        render: (meltShop: string) => <TableCell>{meltShop}</TableCell>
      },
      {
        title: (
          <HeaderColumnTable
            {...{
              width: 160,
              title: 'Laminação',
              column: 'rollingMill',
              value: String(filters.rollingMill),
              options: getColumnOptions({
                key: 'rollingMill',
                sort: true
              }),
              debounce: (e) => debounceFilter(e)
            }}
          />
        ),
        dataIndex: 'rollingMill',
        render: (rollingMill: string) => <TableCell>{rollingMill}</TableCell>
      },
      {
        title: (
          <HeaderColumnTable
            {...{
              width: 160,
              title: 'LA',
              column: 'rollingFinish',
              value: String(filters.rollingFinish),
              options: getColumnOptions({
                key: 'rollingFinish',
                sort: true
              }),
              debounce: (e) => debounceFilter(e)
            }}
          />
        ),
        dataIndex: 'rollingFinish',
        render: (rollingFinish: string) => (
          <TableCell>{rollingFinish}</TableCell>
        )
      },
      {
        title: (
          <HeaderColumnTable
            {...{
              width: 160,
              title: 'Acabamento',
              column: 'finish',
              value: String(filters.finish),
              options: getColumnOptions({
                key: 'finish',
                sort: true
              }),
              debounce: (e) => debounceFilter(e)
            }}
          />
        ),
        dataIndex: 'finish',
        render: (finish: string) => <TableCell>{finish}</TableCell>
      },
      {
        title: (
          <HeaderColumnTable
            {...{
              width: 160,
              title: 'Estoque Usina',
              column: 'stockMill',
              value: String(filters.stockMill),
              options: getColumnOptions({
                key: 'stockMill',
                sort: true
              }),
              debounce: (e) => debounceFilter(e)
            }}
          />
        ),
        dataIndex: 'stockMill',
        render: (stockMill: string) => <TableCell>{stockMill}</TableCell>
      },
      {
        title: (
          <HeaderColumnTable
            {...{
              width: 160,
              title: 'Em transferência',
              column: 'transit',
              value: String(filters.transit),
              options: getColumnOptions({
                key: 'transit',
                sort: true
              }),
              debounce: (e) => debounceFilter(e)
            }}
          />
        ),
        dataIndex: 'transit',
        render: (transit: string) => <TableCell>{transit}</TableCell>
      },
      {
        title: (
          <HeaderColumnTable
            {...{
              width: 160,
              title: 'Estoque entre-posto',
              column: 'stockWarehouse',
              value: String(filters.stockWarehouse),
              options: getColumnOptions({
                key: 'stockWarehouse',
                sort: true
              }),
              debounce: (e) => debounceFilter(e)
            }}
          />
        ),
        dataIndex: 'stockWarehouse',
        render: (stockWarehouse: string) => (
          <TableCell>{stockWarehouse}</TableCell>
        )
      },
      ...(futureDeliveriesColumnQty > 0
        ? Array.from({ length: futureDeliveriesColumnQty })
            .map((_, index) => [
              {
                title: (
                  <HeaderColumnTable
                    {...{
                      width: 200,
                      title: `Data estimada - ${index + 1}`,
                      column: `futureDelivery_${index}_date`,
                      value: filters[`futureDelivery_${index}_date`],
                      options: getColumnOptions({
                        key: `futureDelivery_${index}_date`,
                        sort: true
                      }),
                      debounce: (e) => debounceFilter(e)
                    }}
                  />
                ),
                dataIndex: `futureDelivery_${index}_date`,
                render: (value: string) => <TableCell>{value ?? '-'}</TableCell>
              },
              {
                title: (
                  <HeaderColumnTable
                    {...{
                      width: 180,
                      title: `Qtd. entrega - ${index + 1}`,
                      column: `futureDelivery_${index}_quantity`,
                      value: filters[`futureDelivery_${index}_quantity`],
                      options: getColumnOptions({
                        key: `futureDelivery_${index}_quantity`,
                        sort: true
                      }),
                      debounce: (e) => debounceFilter(e)
                    }}
                  />
                ),
                dataIndex: `futureDelivery_${index}_quantity`,
                render: (value: string) => <TableCell>{value ?? '-'}</TableCell>
              },
              {
                title: (
                  <HeaderColumnTable
                    {...{
                      width: 180,
                      title: `Un. entrega - ${index + 1}`,
                      column: `futureDelivery_${index}_unit`,
                      value: filters[`futureDelivery_${index}_unit`],
                      options: getColumnOptions({
                        key: `futureDelivery_${index}_unit`,
                        sort: true
                      }),
                      debounce: (e) => debounceFilter(e)
                    }}
                  />
                ),
                dataIndex: `futureDelivery_${index}_unit`,
                render: (value: string) => <TableCell>{value ?? '-'}</TableCell>
              }
            ])
            .flatMap((item) => item)
        : [])
    ];

    const hideColumns: string[] = [
      ...orderPreferences.columnsHidden,
      ...(orderedSales.some((item) => item.orderType === EOrderType.PLANOS)
        ? []
        : flatSteelItems)
    ].map((item) => getTargetKeyFromMap(item));

    if (
      customerViewType === ECustomerTypeView.RECEIVER ||
      customerViewType === ECustomerTypeView.FABRICATION_RECEIVER
    ) {
      hideColumns.push('emitter');
      hideColumns.push('receiver');
    }

    return columns
      .filter((col) => !finalHideColumns.includes(col.dataIndex))
      .map((item, index, arr) => {
        if (
          (index === arr.length - 1 || index === arr.length - 2) &&
          React.isValidElement(item.title)
        ) {
          return {
            ...item,
            title: React.cloneElement(
              item.title as React.ReactElement<HeaderColumnProps>,
              {
                lastColumns: true
              }
            )
          };
        }
        return item;
      });
  };

  const rowSelection: TableRowSelection<SalesOrderItemTable> = {
    selectedRowKeys,
    type: 'checkbox',
    onSelectAll: (selected) => {
      if (!selected) {
        setSelectedRowKeys([]);
      }
    },
    onChange: (e, selectedRows, info) => {
      if (e.length >= 10) {
        dispatch(
          addToast({
            type: EToastTypes.WARNING,
            message: 'Máximo de 10 itens por seleção atingido.',
            durationInMS: 5000,
            labelActionOne: 'Fechar'
          })
        );
      }

      if (info.type === 'all') {
        setSelectedRowKeys(selectedRows.slice(0, 10).map((item) => item.key));
        return;
      }

      setSelectedRowKeys(e);
    },
    preserveSelectedRowKeys: true,
    renderCell: (_value, record, _index, originNode) => {
      return record.orderType === EOrderType.PLANOS ? (
        <Tooltip
          title="Esse item não pode ser selecionado no momento. Consulte o seu vendedor."
          placement="right"
        >
          <div>{originNode}</div>
        </Tooltip>
      ) : (
        <>{originNode}</>
      );
    },
    getCheckboxProps: (record) => {
      if (selectedRowKeys.length >= maxSelectedRows) {
        return {
          disabled: !selectedRowKeys.includes(record.key)
        };
      } else if (record.orderType === EOrderType.PLANOS) {
        return {
          disabled: true
        };
      }
      return {
        disabled: false
      };
    }
  };

  const panelOptions = actionsOptions({
    onCancelOrderClick: () => {
      const findItems = orderedSales.filter((order) =>
        selectedRowKeys.includes(order.key)
      );

      if (findItems && findItems.length > 0) {
        dispatch(
          validationUpdateItems({
            itemsSelected: findItems,
            typeAction: 'cancel'
          })
        );
      }

      dispatch(
        sendTagging({
          event: 'clique_acoes',
          click: 'solicitar_cancelamento',
          screenName: 'LG:MeusPedidos',
          itemDescription: 'meus_pedidos_acosLongos',
          estado_emissor:
            ordersState[
              ordersState.tableType || EOrderTab.OPEN
            ].initialInputValues.states.join(', '),
          emissor_pedido: customersState.customers
            .filter((item) =>
              ordersState[
                ordersState.tableType || EOrderTab.OPEN
              ].initialInputValues.emitterIds.includes(item.customerId)
            )
            .map((item) => item.name)
            .join(', '),
          data_inicio: moment(
            ordersState[ordersState.tableType || EOrderTab.OPEN]
              .initialInputValues.startDate
          ).format('DD/MM/YYYY'),
          data_fim: moment(
            ordersState[ordersState.tableType || EOrderTab.OPEN]
              .initialInputValues.endDate
          ).format('DD/MM/YYYY')
        })
      );
    },
    onUpdateOrderClick: () => {
      const findItems = orderedSales.filter((order) =>
        selectedRowKeys.includes(order.key)
      );

      if (findItems && findItems.length > 0) {
        dispatch(
          validationUpdateItems({
            itemsSelected: findItems,
            typeAction: 'update'
          })
        );
      }

      dispatch(
        sendTagging({
          event: 'clique_acoes',
          click: 'solicitar_alteracoes',
          screenName: 'LG:MeusPedidos',
          itemDescription: 'meus_pedidos_acosLongos',
          estado_emissor:
            ordersState[
              ordersState.tableType || EOrderTab.OPEN
            ].initialInputValues.states.join(', '),
          emissor_pedido: customersState.customers
            .filter((item) =>
              ordersState[
                ordersState.tableType || EOrderTab.OPEN
              ].initialInputValues.emitterIds.includes(item.customerId)
            )
            .map((item) => item.name)
            .join(', '),
          data_inicio: moment(
            ordersState[ordersState.tableType || EOrderTab.OPEN]
              .initialInputValues.startDate
          ).format('DD/MM/YYYY'),
          data_fim: moment(
            ordersState[ordersState.tableType || EOrderTab.OPEN]
              .initialInputValues.endDate
          ).format('DD/MM/YYYY')
        })
      );
    },
    onDownloadOrderClick: () => {
      const findItems = orderedSales.filter((order) =>
        selectedRowKeys.includes(order.key)
      );

      if (findItems && findItems.length > 0) {
        dispatch(downloadOrderItems({ downloadsItemsSelected: findItems }));

        // Por limitação de requisiçÕes em massa foi desabilitado essa função
        // if (findItems.length > 1) {
        //   return history.push(
        //     `${ERoutes.ORDERS}/${EPurchaseRoutes.LONG_STEEL}?downloads=bulk`
        //   );
        // }

        history.push(
          `${ERoutes.ORDERS}/${EPurchaseRoutes.LONG_STEEL}?downloads`
        );
      }

      dispatch(
        sendTagging({
          event: 'clique_acoes',
          click: 'baixar_documentos',
          screenName: 'LG:MeusPedidos',
          itemDescription: 'meus_pedidos_acosLongos',
          estado_emissor:
            ordersState[
              ordersState.tableType || EOrderTab.OPEN
            ].initialInputValues.states.join(', '),
          emissor_pedido: customersState.customers
            .filter((item) =>
              ordersState[
                ordersState.tableType || EOrderTab.OPEN
              ].initialInputValues.emitterIds.includes(item.customerId)
            )
            .map((item) => item.name)
            .join(', '),
          data_inicio: moment(
            ordersState[ordersState.tableType || EOrderTab.OPEN]
              .initialInputValues.startDate
          ).format('DD/MM/YYYY'),
          data_fim: moment(
            ordersState[ordersState.tableType || EOrderTab.OPEN]
              .initialInputValues.endDate
          ).format('DD/MM/YYYY')
        })
      );
    },
    disableDownloadOrderFn: orderedSales
      .filter((order) => selectedRowKeys.includes(order.key))
      .every((item) => {
        if (!item.billing || item.billing.length === 0) {
          return true;
        }

        if (selectedRowKeys.length > 1) {
          return true;
        }

        const hasBlockingCondition = item.billing.some(
          (billingItem) =>
            billingItem.canDownloadNF ||
            billingItem.canDownloadQC ||
            billingItem.invoiceZvoi
        );

        return !hasBlockingCondition;
      }),
    // Por limitação de requisiçÕes em massa foi desabilitado essa função
    // orderedSales
    //   .filter((order) => selectedRowKeys.includes(order.key))
    //   .every((item) => {
    //     if (!item.billing || item.billing.length === 0) {
    //       return true;
    //     }

    //     const hasBlockingCondition = item.billing.some(
    //       (billingItem) =>
    //         billingItem.canDownloadNF ||
    //         billingItem.canDownloadQC ||
    //         billingItem.invoiceZvoi
    //     );

    //     return !hasBlockingCondition;
    //   }),
    disableCancelOrderFn: orderedSales
      .filter((order) => selectedRowKeys.includes(order.key))
      .every((item) => item.orderType === EOrderType.PLANOS),
    disableUpdateOrderFn: orderedSales
      .filter((order) => selectedRowKeys.includes(order.key))
      .every((item) => item.orderType === EOrderType.PLANOS)
  });

  if (
    !loadingState &&
    hasInitialized &&
    (!ordersItems || ordersItems?.length === 0)
  ) {
    return (
      <MessageChooseContract>
        <MessageWithoutEmitter>
          Nenhum pedido encontrado <br />
          para os filtros selecionados.
        </MessageWithoutEmitter>
      </MessageChooseContract>
    );
  }

  if (!hasInitialized) {
    return null;
  }

  const handleUpdatePreferences = (preferences: OrderPreferencesProps) => {
    const newToggles = orderViewPreferenceDefault.toggles.map((item) => {
      if (item.value === 'sku') {
        return {
          value: item.value,
          checked: preferences.materialCode === 'sku'
        };
      }

      if (item.value === 'customerSKU') {
        return {
          value: item.value,
          checked: preferences.materialCode === 'customerSKU'
        };
      }

      return {
        value: item.value,
        checked: !preferences.columnsHidden.includes(item.value)
      };
    });

    const newPreferences = {
      name: EUserPreferenceType.ORDER_VIEW,
      toggles: newToggles
    };

    const orderEntryIdx = user.preferences.findIndex(
      (preference) => preference.name === EUserPreferenceType.ORDER_VIEW
    );
    const hasPreferences = user.preferences[
      orderEntryIdx
    ] as IOrderViewPreference;

    const hasChanged =
      JSON.stringify(hasPreferences?.toggles) !==
      JSON.stringify(newPreferences?.toggles);

    if (hasChanged || !hasPreferences) {
      const saveUserProps: ISaveUser = {
        user: user,
        userPreference: newPreferences
      };
      dispatch(saveUser(saveUserProps));
    }
    setOrderPreferences({
      columnsHidden: preferences.columnsHidden.filter(
        (item) => !['sku', 'customerSKU'].includes(item)
      ),
      materialCode: preferences.materialCode
    });

    setModal((prev) => ({ ...prev, personalizeTable: false }));

    dispatch(
      sendTagging({
        event: 'modal_personalizar_tabela',
        click: 'Salvar personalização',
        screenName: 'LG:MeusPedidos',
        itemDescription: 'meus_pedidos_acosLongos',
        estado_emissor:
          ordersState[
            ordersState.tableType || EOrderTab.OPEN
          ].initialInputValues.states.join(', '),
        emissor_pedido: customersState.customers
          .filter((item) =>
            ordersState[
              ordersState.tableType || EOrderTab.OPEN
            ].initialInputValues.emitterIds.includes(item.customerId)
          )
          .map((item) => item.name)
          .join(', '),
        data_inicio: moment(
          ordersState[ordersState.tableType || EOrderTab.OPEN]
            .initialInputValues.startDate
        ).format('DD/MM/YYYY'),
        data_fim: moment(
          ordersState[ordersState.tableType || EOrderTab.OPEN]
            .initialInputValues.endDate
        ).format('DD/MM/YYYY')
      })
    );
  };

  const cleanFilters = () => {
    setFilters({
      ...INITIAL_FILTERS,
      ...(futureDeliveriesColumnQty > 0
        ? createDynamicDeliveryFilters(futureDeliveriesColumnQty)
        : {})
    });
    filterData({
      ...INITIAL_FILTERS,
      ...(futureDeliveriesColumnQty > 0
        ? createDynamicDeliveryFilters(futureDeliveriesColumnQty)
        : {})
    });
    setSelectedRowKeys([]);
  };

  const handleShowPersonalizeModal = () => {
    setModal((prev) => ({ ...prev, personalizeTable: true }));

    dispatch(
      sendTagging({
        event: 'personalizar_tabela',
        screenName: 'LG:MeusPedidos',
        itemDescription: 'meus_pedidos_acosLongos',
        estado_emissor:
          ordersState[
            ordersState.tableType || EOrderTab.OPEN
          ].initialInputValues.states.join(', '),
        emissor_pedido: customersState.customers
          .filter((item) =>
            ordersState[
              ordersState.tableType || EOrderTab.OPEN
            ].initialInputValues.emitterIds.includes(item.customerId)
          )
          .map((item) => item.name)
          .join(', '),
        data_inicio: moment(
          ordersState[ordersState.tableType || EOrderTab.OPEN]
            .initialInputValues.startDate
        ).format('DD/MM/YYYY'),
        data_fim: moment(
          ordersState[ordersState.tableType || EOrderTab.OPEN]
            .initialInputValues.endDate
        ).format('DD/MM/YYYY')
      })
    );
  };

  const handleClosePersonalizeModal = () => {
    setModal((prev) => ({ ...prev, personalizeTable: false }));

    dispatch(
      sendTagging({
        event: 'modal_personalizar_tabela',
        click: 'Cancelar',
        screenName: 'LG:MeusPedidos',
        itemDescription: 'meus_pedidos_acosLongos',
        estado_emissor:
          ordersState[
            ordersState.tableType || EOrderTab.OPEN
          ].initialInputValues.states.join(', '),
        emissor_pedido: customersState.customers
          .filter((item) =>
            ordersState[
              ordersState.tableType || EOrderTab.OPEN
            ].initialInputValues.emitterIds.includes(item.customerId)
          )
          .map((item) => item.name)
          .join(', '),
        data_inicio: moment(
          ordersState[ordersState.tableType || EOrderTab.OPEN]
            .initialInputValues.startDate
        ).format('DD/MM/YYYY'),
        data_fim: moment(
          ordersState[ordersState.tableType || EOrderTab.OPEN]
            .initialInputValues.endDate
        ).format('DD/MM/YYYY')
      })
    );
  };

  const disabledCleanFilters =
    selectedRowKeys.length === 0 &&
    Object.values(filters).every((item) => !item);

  return (
    <OrdersTableContainer>
      <TableHeaderContainer>
        <TableHeaderContainerButtons>
          <Button
            onClick={handleShowPersonalizeModal}
            name="personalize-table"
            variant="secondary"
          >
            Personalizar tabela
          </Button>
          <Button
            disabled={disabledCleanFilters}
            name="clean-filters"
            variant="secondary"
            onClick={cleanFilters}
          >
            Limpar filtros
          </Button>
        </TableHeaderContainerButtons>

        <TableHeaderActionButton>
          <Button
            disabled={selectedRowKeys.length === 0}
            variant="secondary"
            name="actions-button"
          >
            <InformationPanel
              trigger={
                <div>
                  <Button
                    name="action-information-panel"
                    variant="neutral"
                    icon={<Icon option="MoreVertical"></Icon>}
                  >
                    <span>Ações</span>
                  </Button>
                </div>
              }
              position="bottom right"
              panelOptions={panelOptions}
            />
          </Button>
        </TableHeaderActionButton>
      </TableHeaderContainer>
      <TableContainer>
        <Table
          scroll={{ x: '300px' }}
          columns={getColumns()}
          data={orderedSales}
          rowSelection={rowSelection}
          hasPagination
        />
      </TableContainer>

      <OrdersPersonalizeModal
        orderPreferences={orderPreferences}
        canShow={modal.personalizeTable}
        onClose={handleClosePersonalizeModal}
        onUpdate={handleUpdatePreferences}
        hasFlatsteel={orderedSales.some(
          (item) => item.orderType === EOrderType.PLANOS
        )}
        hasFutureDeliveries={futureDeliveriesColumnQty > 0}
      />
    </OrdersTableContainer>
  );
};
